import type { ECharts } from "echarts";
import ReactECharts from "echarts-for-react";

type Point = {
  readonly month: string;
  readonly value: number | null | undefined;
};

type Series = {
  name: string;
  points: ReadonlyArray<Point>;
  type: "line" | "bar";
  color?: string;
  stack?: string;
  // Render this line dashed (e.g. a forecast continuation). Line series only.
  dashed?: boolean;
};

export default function KpiChart({
  title,
  series,
  format,
  gapMonths,
  referenceLine,
  formatXLabel,
  onMonthClick,
  onMonthRange,
  onBarClick,
  onPointClick,
  showPointSymbols,
}: {
  title: string;
  series: Series[];
  format: "money" | "percent" | "number";
  gapMonths?: ReadonlyArray<string>;
  // Optional: a dashed horizontal benchmark line (e.g. the "40" in Rule of 40).
  referenceLine?: { value: number; label?: string };
  // Optional: maps an x-axis month key to a display label (e.g. "2025-06" →
  // "Q2 '25"). Display-only — click resolution still uses the underlying month.
  formatXLabel?: (month: string) => string;
  onMonthClick: (month: string) => void;
  // Optional: a click-drag across bars selects a contiguous month range.
  // When omitted, the chart is click-only (existing consumers are unaffected).
  onMonthRange?: (from: string, to: string) => void;
  // Optional: a click on a specific bar reports both the series name and the
  // month (echarts series click), for per-series drill-down. Line series and
  // empty plot clicks don't fire it. When omitted, existing consumers are
  // unaffected (the axis-level onMonthClick still fires).
  onBarClick?: (seriesName: string, month: string) => void;
  // Optional: a click on a specific LINE point reports both the series name and
  // the month — the line-series analogue of onBarClick (echarts fires distinct
  // events for bar segments vs line points). When omitted, existing consumers
  // are unaffected.
  onPointClick?: (seriesName: string, month: string) => void;
  // Optional: force point symbols on the (non-dashed) lines, overriding the
  // auto-thin heuristic. Symbols are echarts' click target, so a chart whose
  // x-axis exceeds the auto threshold but still needs onPointClick must set this.
  showPointSymbols?: boolean;
}) {
  const months = series[0].points.map((p) => p.month);
  const gaps = new Set(gapMonths ?? []);
  // Statement-coverage gap shading, hoisted so the drag preview can re-apply it
  // alongside the live selection band without clobbering it.
  const gapAreaData = months
    .filter((m) => gaps.has(m))
    .map((m) => [
      { xAxis: m, itemStyle: { color: "rgba(245, 158, 11, 0.12)" } },
      { xAxis: m },
    ]);

  // Axis labels stay compact (k / M, whole percent) so they don't crowd the
  // plot; the tooltip shows the exact figure.
  const formatAxis = (value: number) => {
    if (format === "percent") return `${(value * 100).toFixed(0)}%`;
    if (format === "money") {
      const abs = Math.abs(value);
      // Adaptive units: plain dollars for small values (e.g. ARPU), k for
      // thousands, M for millions — so a $120 ARPU shows "$120", not "$0k".
      let text: string;
      if (abs >= 1_000_000) text = `${(abs / 1_000_000).toFixed(1)}M`;
      else if (abs >= 1_000) text = `${(abs / 1_000).toFixed(0)}k`;
      else text = abs.toFixed(0);
      return `${value < 0 ? "-" : ""}$${text}`;
    }
    return value.toFixed(0);
  };

  // Tooltip shows full precision: money to the dollar with separators,
  // NRR/GRR and other percentages to one decimal place.
  const formatTooltip = (value: number) => {
    if (format === "percent") return `${(value * 100).toFixed(1)}%`;
    if (format === "money") {
      return `${value < 0 ? "-" : ""}$${Math.round(
        Math.abs(value)
      ).toLocaleString()}`;
    }
    return Math.round(value).toLocaleString();
  };

  const option = {
    title: { text: title, textStyle: { fontSize: 13, fontWeight: 600 } },
    tooltip: {
      trigger: "axis",
      valueFormatter: (value: number | null) =>
        value == null ? "—" : formatTooltip(value),
    },
    grid: { left: 56, right: 16, top: 36, bottom: 24 },
    xAxis: {
      type: "category",
      data: months,
      axisLabel: formatXLabel ? { formatter: formatXLabel } : undefined,
    },
    yAxis: {
      type: "value",
      axisLabel: { formatter: formatAxis },
    },
    series: series.map((s, index) => ({
      name: s.name,
      type: s.type,
      data: s.points.map((p) => p.value ?? null),
      itemStyle: s.color ? { color: s.color } : undefined,
      // A dashed forecast continuation; nulls (the actual portion) break the line
      // so it starts where the solid series ends.
      lineStyle: s.dashed ? { type: "dashed" } : undefined,
      connectNulls: false,
      // Sparse series (quarterly/yearly views) would be near-invisible as a
      // bare line, so show point symbols once the series thins out. Forecast
      // lines stay symbol-free so the dashes read clearly.
      showSymbol: s.dashed ? false : (showPointSymbols ?? months.length <= 24),
      barGap: s.stack ? undefined : "-100%",
      stack: s.stack,
      // Shade months with known statement-coverage gaps
      markArea:
        index === 0 && gaps.size > 0
          ? { silent: true, data: gapAreaData }
          : undefined,
      // Dashed benchmark line (e.g. the 40 in Rule of 40), drawn once.
      markLine:
        index === 0 && referenceLine
          ? {
              silent: true,
              symbol: "none",
              lineStyle: { type: "dashed", color: "#9ca3af" },
              label: {
                position: "insideEndTop",
                color: "#6b7280",
                fontSize: 10,
                formatter: referenceLine.label ?? String(referenceLine.value),
              },
              data: [{ yAxis: referenceLine.value }],
            }
          : undefined,
    })),
  };

  // One ZRender state machine: mousedown→up at the same bar is a single-month
  // click; dragging across bars selects a contiguous month range. We bind
  // mousedown/move/up (not "click") so a trailing click event can't double-fire.
  const onChartReady = (chart: ECharts) => {
    const zr = chart.getZr();
    let startIndex: number | null = null;

    // Per-bar drill-down: echarts' high-level series click carries the series
    // name and the category (month). Only bar segments fire it; line series and
    // empty-plot clicks are ignored. Coexists with the zr drag/click machinery
    // below (which drives the axis-level onMonthClick/onMonthRange).
    if (onBarClick) {
      chart.on(
        "click",
        (params: { componentType?: string; seriesType?: string; seriesName?: string; name?: string }) => {
          if (params.componentType === "series" && params.seriesType === "bar" && params.seriesName && params.name) {
            onBarClick(params.seriesName, params.name);
          }
        }
      );
    }

    // Per-point drill on line series (the income-curve point selection): echarts'
    // series click on a line carries the series name + the category (month).
    if (onPointClick) {
      chart.on(
        "click",
        (params: { componentType?: string; seriesType?: string; seriesName?: string; name?: string }) => {
          if (params.componentType === "series" && params.seriesType === "line" && params.seriesName && params.name) {
            onPointClick(params.seriesName, params.name);
          }
        }
      );
    }

    const indexAt = (offsetX: number, offsetY: number): number | null => {
      const point = chart.convertFromPixel({ seriesIndex: 0 }, [offsetX, offsetY]);
      if (!point) return null;
      const i = Math.round(point[0]);
      return i >= 0 && i < months.length ? i : null;
    };

    // Live drag preview: re-apply the gap shading plus a selection band on
    // series[0], imperatively (not via the option prop) to dodge the known
    // echarts merge-on-removal bug. Passing `undefined` clears the band.
    const renderPreview = (lo?: number, hi?: number) => {
      const band =
        lo != null && hi != null
          ? [
              [
                { xAxis: months[lo], itemStyle: { color: "rgba(59, 130, 246, 0.15)" } },
                { xAxis: months[hi] },
              ],
            ]
          : [];
      chart.setOption({
        series: [{ markArea: { silent: true, data: [...gapAreaData, ...band] } }],
      });
    };

    zr.on("mousedown", (event: { offsetX: number; offsetY: number }) => {
      startIndex = indexAt(event.offsetX, event.offsetY);
    });

    zr.on("mousemove", (event: { offsetX: number; offsetY: number }) => {
      if (startIndex == null || !onMonthRange) return;
      const current = indexAt(event.offsetX, event.offsetY);
      if (current == null) return;
      renderPreview(Math.min(startIndex, current), Math.max(startIndex, current));
    });

    zr.on("mouseup", (event: { offsetX: number; offsetY: number }) => {
      if (startIndex == null) return;
      const endIndex = indexAt(event.offsetX, event.offsetY) ?? startIndex;
      const lo = Math.min(startIndex, endIndex);
      const hi = Math.max(startIndex, endIndex);
      startIndex = null;
      renderPreview(); // clear the live band
      if (lo === hi || !onMonthRange) onMonthClick(months[lo]);
      else onMonthRange(months[lo], months[hi]);
    });
  };

  return (
    <div className="bg-white rounded border p-3">
      <ReactECharts
        option={option}
        style={{ height: 260 }}
        onChartReady={onChartReady}
      />
    </div>
  );
}
